import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type SelectHTMLAttributes,
} from 'react'

import { cn } from '@/utils/cn'

import { baseControl } from './styles'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
}

/** Mostra o texto completo da opção escolhida ao passar o mouse (quando o campo corta). */
function syncTitle(select: HTMLSelectElement | null) {
  if (select) select.title = select.selectedOptions[0]?.text ?? ''
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, onChange, ...props },
  forwardedRef,
) {
  const innerRef = useRef<HTMLSelectElement | null>(null)
  const setRef = useCallback(
    (node: HTMLSelectElement | null) => {
      innerRef.current = node
      if (typeof forwardedRef === 'function') forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef],
  )

  // Um `title` explícito (ex.: explicação do campo) tem prioridade sobre a dica automática.
  const autoTitle = props.title === undefined

  // Valor controlado ou opções que chegam depois (lista da API) também atualizam a dica.
  useLayoutEffect(() => {
    if (autoTitle) syncTitle(innerRef.current)
  })

  return (
    <select
      ref={setRef}
      aria-invalid={invalid || undefined}
      className={cn(
        baseControl,
        'truncate pr-8',
        invalid ? 'border-critical' : 'border-line',
        className,
      )}
      onChange={(event: ChangeEvent<HTMLSelectElement>) => {
        if (autoTitle) syncTitle(event.currentTarget)
        onChange?.(event)
      }}
      {...props}
    >
      {children}
    </select>
  )
})
